import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Title, useNotify } from "react-admin";

import { adminApi } from "../dataProvider";

type ProcessAxes = Record<string, { options: string[]; description?: string }>;
type ProcessConfigDef = {
  value: Record<string, string>;
  axes: ProcessAxes;
  broker_needed?: boolean;
  description?: string;
};
type SettingDef = { value: string | number; options?: string[]; kind?: string; description?: string };
type SherpConfigDef = {
  value: Record<string, string>;
  fields: Record<string, string>;
  description?: string;
};
// поля реферальной программы описаны богаче обычного объекта: у каждого свой тип контрола
type ReferralFieldDef = {
  label: string;
  kind: "number" | "text" | "textarea" | "bool" | "select";
  options?: string[];
  option_labels?: Record<string, string>;
};
type ReferralConfigDef = {
  value: Record<string, string>;
  fields: Record<string, ReferralFieldDef>;
  description?: string;
};
type SettingsResp = {
  process_config: ProcessConfigDef;
  order_min_rub: SettingDef;
  order_max_rub: SettingDef;
  rate_markup_pct: SettingDef;
  withdraw_crypto_fee: SettingDef;
  broker_payout_wallet: SettingDef;
  broker_withdraw_enabled: SettingDef;
  card_refund_discount_pct: SettingDef;
  card_exchange_fee_pct: SettingDef;
  card_crypto_fee_pct: SettingDef;
  card_exchange_min_rub: SettingDef;
  card_exchange_max_rub: SettingDef;
  manager_chat_id: SettingDef;
  sherp_config: SherpConfigDef;
  payment_requisites: SherpConfigDef;
  referral_config: ReferralConfigDef;
};

const AXIS_TITLES: Record<string, string> = {
  data_source: "Сбор данных",
  kyc_approval: "Одобрение KYC",
  doc_signing: "Подписание оферты",
  requisites: "Реквизиты оплаты",
  payment_confirmation: "Подтверждение платежа",
  sherp_joinder: "Договор присоединения ШЭРП",
};

const OPTION_LABELS: Record<string, Record<string, string>> = {
  data_source: { internal: "Свои данные (ИИ: загрузка + OCR)", sumsub: "Sumsub", idenfy: "iDenfy" },
  kyc_approval: { manual: "Оператор (вручную)", provider: "Провайдер (GREEN/APPROVED)", broker: "Брокер (API)" },
  doc_signing: { off: "Выключено", broker: "Через API брокера" },
  requisites: { manual: "Фиксированные", broker: "Из API брокера" },
  payment_confirmation: { manual: "Оператор (вручную)", api: "Авто через API брокера" },
  sherp_joinder: { off: "Выключено", sherp: "Подпись по email-OTP" },
};

const AXIS_ORDER = ["data_source", "kyc_approval", "doc_signing", "requisites", "payment_confirmation", "sherp_joinder"];

export function SettingsPage() {
  const notify = useNotify();
  const [settings, setSettings] = useState<SettingsResp | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await adminApi<SettingsResp>("/admin/settings");
        setSettings(data);
      } catch (e) {
        notify(e instanceof Error ? e.message : "Failed to load settings", { type: "error" });
      }
    })();
  }, [notify]);

  const updateLimit = async (key: string, value: number) => {
    if (!settings) return;
    setSaving(key);
    try {
      const saved = await adminApi<{ value: string | number }>(`/admin/settings/${key}`, {
        method: "PUT",
        json: { value },
      });
      setSettings({ ...settings, [key]: { ...settings[key as keyof SettingsResp], value: saved.value } } as SettingsResp);
      notify(`Сохранено: ${key} → ${saved.value}`, { type: "success" });
    } catch (e) {
      notify(e instanceof Error ? e.message : "Не удалось сохранить", { type: "error" });
    } finally {
      setSaving(null);
    }
  };

  // строковая настройка (напр. id чата менеджеров) — тот же PUT, значение строкой
  const updateSetting = async (key: string, value: string) => {
    if (!settings) return;
    setSaving(key);
    try {
      const saved = await adminApi<{ value: string | number }>(`/admin/settings/${key}`, {
        method: "PUT",
        json: { value },
      });
      setSettings({ ...settings, [key]: { ...settings[key as keyof SettingsResp], value: saved.value } } as SettingsResp);
      notify("Сохранено", { type: "success" });
    } catch (e) {
      notify(e instanceof Error ? e.message : "Не удалось сохранить", { type: "error" });
    } finally {
      setSaving(null);
    }
  };

  // булева настройка (переключатель) — тот же PUT, значение boolean
  const updateBool = async (key: string, value: boolean) => {
    if (!settings) return;
    setSaving(key);
    try {
      const saved = await adminApi<{ value: boolean }>(`/admin/settings/${key}`, {
        method: "PUT",
        json: { value },
      });
      setSettings({ ...settings, [key]: { ...settings[key as keyof SettingsResp], value: saved.value } } as SettingsResp);
      notify("Сохранено", { type: "success" });
    } catch (e) {
      notify(e instanceof Error ? e.message : "Не удалось сохранить", { type: "error" });
    } finally {
      setSaving(null);
    }
  };

  const updateAxis = async (axis: string, value: string) => {
    if (!settings) return;
    setSaving(axis);
    try {
      const next = { ...settings.process_config.value, [axis]: value };
      const saved = await adminApi<{ value: Record<string, string> }>("/admin/settings/process_config", {
        method: "PUT",
        json: { value: next },
      });
      setSettings({
        ...settings,
        process_config: { ...settings.process_config, value: saved.value },
      });
      notify(`Сохранено: ${AXIS_TITLES[axis] ?? axis} → ${value}`, { type: "success" });
    } catch (e) {
      notify(e instanceof Error ? e.message : "Не удалось сохранить", { type: "error" });
    } finally {
      setSaving(null);
    }
  };

  const saveObject = (
    key: "sherp_config" | "payment_requisites" | "referral_config",
    label: string,
  ) => async (value: Record<string, string>) => {
    if (!settings) return;
    setSaving(key);
    try {
      const saved = await adminApi<{ value: Record<string, string> }>(`/admin/settings/${key}`, {
        method: "PUT",
        json: { value },
      });
      setSettings({ ...settings, [key]: { ...settings[key], value: saved.value } });
      notify(`Сохранено: ${label}`, { type: "success" });
    } catch (e) {
      notify(e instanceof Error ? e.message : "Не удалось сохранить", { type: "error" });
    } finally {
      setSaving(null);
    }
  };

  const cfg = settings?.process_config;
  const brokerNeeded = cfg?.broker_needed;

  return (
    <Box sx={{ maxWidth: 700, mt: 2 }}>
      <Title title="Настройки" />

      <Card sx={{ mb: 2 }}>
        <CardHeader title="Процесс клиента" subheader="Независимые шаги: сбор данных, одобрение, подписание, реквизиты" />
        <CardContent>
          {!settings && <Typography variant="body2">Загрузка…</Typography>}
          {cfg && (
            <Stack spacing={2}>
              {AXIS_ORDER.filter((a) => cfg.axes[a]).map((axis) => (
                <FormControl key={axis} fullWidth>
                  <InputLabel id={`axis-${axis}`}>{AXIS_TITLES[axis] ?? axis}</InputLabel>
                  <Select
                    labelId={`axis-${axis}`}
                    label={AXIS_TITLES[axis] ?? axis}
                    value={cfg.value[axis] ?? ""}
                    disabled={saving === axis}
                    onChange={(e) => updateAxis(axis, e.target.value)}
                  >
                    {cfg.axes[axis].options.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {OPTION_LABELS[axis]?.[opt] ?? opt}
                      </MenuItem>
                    ))}
                  </Select>
                  {cfg.axes[axis].description && (
                    <FormHelperText>{cfg.axes[axis].description}</FormHelperText>
                  )}
                </FormControl>
              ))}
              <Alert severity={brokerNeeded ? "info" : "warning"}>
                {brokerNeeded
                  ? "Включена брокер-ось — клиент онбордится у брокера (реквизиты/подписание/одобрение)."
                  : "Брокер не задействован — реквизиты фиксированные, онбординга к брокеру нет."}
              </Alert>
            </Stack>
          )}
        </CardContent>
      </Card>

      {settings && (
        <Card>
          <CardHeader title="Лимиты заявок" subheader="Сумма покупки в рублях" />
          <CardContent>
            <Stack direction="row" spacing={2}>
              <LimitField
                label="Минимум, ₽"
                initial={Number(settings.order_min_rub.value)}
                saving={saving === "order_min_rub"}
                onSave={(v) => updateLimit("order_min_rub", v)}
              />
              <LimitField
                label="Максимум, ₽"
                initial={Number(settings.order_max_rub.value)}
                saving={saving === "order_max_rub"}
                onSave={(v) => updateLimit("order_max_rub", v)}
              />
            </Stack>
            <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
              Изменения применяются сразу — и в боте, и в веб-приложении.
            </Typography>
          </CardContent>
        </Card>
      )}

      {settings && (
        <Card sx={{ mt: 2 }}>
          <CardHeader title="Курс" subheader="Источник — cifra.by (USDT/RUB), клиенту показывается с наценкой" />
          <CardContent>
            <LimitField
              label="Наценка, %"
              initial={Number(settings.rate_markup_pct.value)}
              saving={saving === "rate_markup_pct"}
              onSave={(v) => updateLimit("rate_markup_pct", v)}
              min={0}
            />
            <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
              Итоговый курс = биржевой курс cifra.by + наценка. Применяется и к отображению, и к
              расчёту суммы заявки. Изменения применяются сразу.
            </Typography>
          </CardContent>
        </Card>
      )}

      {settings && (
        <Card sx={{ mt: 2 }}>
          <CardHeader title="Вывод средств" subheader="Комиссия за вывод на крипто-кошелёк (TRC20)" />
          <CardContent>
            <LimitField
              label="Комиссия крипто-вывода, USD"
              initial={Number(settings.withdraw_crypto_fee.value)}
              saving={saving === "withdraw_crypto_fee"}
              onSave={(v) => updateLimit("withdraw_crypto_fee", v)}
              min={0}
            />
            <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
              Списывается при выводе на TRC20-кошелёк: клиент получает (сумма − комиссия). Для вывода
              на карту и по SWIFT комиссия не берётся. Изменения применяются сразу.
            </Typography>
            <FormControlLabel
              sx={{ mt: 1, display: "block" }}
              control={
                <Switch
                  checked={Boolean(settings.broker_withdraw_enabled.value)}
                  disabled={saving === "broker_withdraw_enabled"}
                  onChange={(e) => updateBool("broker_withdraw_enabled", e.target.checked)}
                />
              }
              label="Приём крипто-выводов включён"
            />
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 1 }}>
              Выключи, когда брокер недоступен: новые выводы (RUB→крипта и с карты) не принимаются,
              клиент получает «Сервис временно недоступен», ничего не списывается.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <TextSettingField
                label="Платёжный кош брокера (Tron-адрес)"
                initial={String(settings.broker_payout_wallet.value ?? "")}
                saving={saving === "broker_payout_wallet"}
                onSave={(v) => updateSetting("broker_payout_wallet", v)}
              />
              <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
                Кошелёк, с которого брокер отправляет USDT-выводы на кастодиалы. Если задан — перед
                каждым выводом автоматически доливаем на него энергию/bandwidth (65k если у кастодиала
                уже есть USDT, иначе 131k), чтобы его перевод не упал из-за нехватки ресурсов. Стоимость
                аренды помечается в истории «Аренда энергии» как в пользу брокера — для помесячного
                счёта. Пусто — долив выключен.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {settings && (
        <Card sx={{ mt: 2 }}>
          <CardHeader
            title="Виртуальная карта"
            subheader="Курсы обменов со счётом, комиссии и лимиты выпущенной карты"
          />
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <LimitField
                  label="Скидка курса возврата, %"
                  initial={Number(settings.card_refund_discount_pct.value)}
                  saving={saving === "card_refund_discount_pct"}
                  onSave={(v) => updateLimit("card_refund_discount_pct", v)}
                  min={0}
                />
                <LimitField
                  label="Комиссия обмена ₽↔карта, %"
                  initial={Number(settings.card_exchange_fee_pct.value)}
                  saving={saving === "card_exchange_fee_pct"}
                  onSave={(v) => updateLimit("card_exchange_fee_pct", v)}
                  min={0}
                />
                <LimitField
                  label="Курс USDT: отклонение от 1:1, %"
                  initial={Number(settings.card_crypto_fee_pct.value)}
                  saving={saving === "card_crypto_fee_pct"}
                  onSave={(v) => updateLimit("card_crypto_fee_pct", v)}
                  min={0}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <LimitField
                  label="Минимум обмена, ₽"
                  initial={Number(settings.card_exchange_min_rub.value)}
                  saving={saving === "card_exchange_min_rub"}
                  onSave={(v) => updateLimit("card_exchange_min_rub", v)}
                />
                <LimitField
                  label="Максимум обмена, ₽"
                  initial={Number(settings.card_exchange_max_rub.value)}
                  saving={saving === "card_exchange_max_rub"}
                  onSave={(v) => updateLimit("card_exchange_max_rub", v)}
                />
              </Stack>
              <TextSettingField
                label="Чат менеджеров (ID)"
                initial={String(settings.manager_chat_id.value ?? "")}
                saving={saving === "manager_chat_id"}
                onSave={(v) => updateSetting("manager_chat_id", v)}
              />
            </Stack>
            <Typography variant="caption" sx={{ mt: 1.5, display: "block", color: "text.secondary" }}>
              Пополнение карты идёт по курсу с главной (биржевой + наценка), возврат на счёт — по
              «биржевой − скидка». Комиссия обмена удерживается сверх курса, одинаково в обе стороны.
              Вывод с карты в USDT: 0% отклонения — ровно 1:1 ($100 с карты = 100 USDT), 2% — клиент
              получает 98 USDT со $100; сверх этого удерживаются фиксированные{" "}
              {Number(settings.withdraw_crypto_fee.value)} USDT (поле «Комиссия крипто-вывода»). В чат
              менеджеров приходят задачи на откуп USDT; пусто — уходят в общую группу заявок.
            </Typography>
          </CardContent>
        </Card>
      )}

      {settings?.sherp_config && (
        <Card sx={{ mt: 2 }}>
          <CardHeader
            title="Договор присоединения ШЭРП"
            subheader={settings.sherp_config.description ?? "Лицензии, банковские реквизиты, лимиты"}
          />
          <CardContent>
            <SherpConfigForm
              fields={settings.sherp_config.fields}
              value={settings.sherp_config.value}
              saving={saving === "sherp_config"}
              onSave={saveObject("sherp_config", "настройки ШЭРП")}
            />
          </CardContent>
        </Card>
      )}

      {settings?.referral_config && (
        <Card sx={{ mt: 2 }}>
          <CardHeader
            title="Реферальная программа"
            subheader={settings.referral_config.description ?? "Ставки, холд, выплаты партнёрам"}
          />
          <CardContent>
            <ReferralConfigForm
              fields={settings.referral_config.fields}
              value={settings.referral_config.value}
              saving={saving === "referral_config"}
              onSave={saveObject("referral_config", "настройки реферальной программы")}
            />
          </CardContent>
        </Card>
      )}

      {settings?.payment_requisites && (
        <Card sx={{ mt: 2 }}>
          <CardHeader
            title="Реквизиты для оплаты"
            subheader={settings.payment_requisites.description ?? "Банковский перевод, выдаётся клиенту"}
          />
          <CardContent>
            <SherpConfigForm
              fields={settings.payment_requisites.fields}
              value={settings.payment_requisites.value}
              saving={saving === "payment_requisites"}
              onSave={saveObject("payment_requisites", "реквизиты оплаты")}
              caption="Эти реквизиты показываются клиенту для оплаты заявки (фиксированный режим)."
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

function SherpConfigForm({
  fields,
  value,
  saving,
  onSave,
  caption = "Эти значения подставляются в PDF Договора присоединения и Заявления.",
}: {
  fields: Record<string, string>;
  value: Record<string, string>;
  saving: boolean;
  onSave: (value: Record<string, string>) => void;
  caption?: string;
}) {
  const [draft, setDraft] = useState<Record<string, string>>(value);
  useEffect(() => setDraft(value), [value]);
  const dirty = Object.keys(fields).some((k) => (draft[k] ?? "") !== (value[k] ?? ""));

  return (
    <Stack spacing={2}>
      {Object.entries(fields).map(([key, label]) => (
        <TextField
          key={key}
          label={label}
          value={draft[key] ?? ""}
          disabled={saving}
          onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
          fullWidth
          size="small"
        />
      ))}
      <Box>
        <button
          type="button"
          disabled={saving || !dirty}
          onClick={() => onSave(draft)}
          style={{ padding: "8px 16px", cursor: saving || !dirty ? "default" : "pointer" }}
        >
          {saving ? "Сохранение…" : "Сохранить"}
        </button>
      </Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {caption}
      </Typography>
    </Stack>
  );
}

// Форма реферальной программы: контрол подбирается по kind поля, а не по угадыванию.
// Пустая строка в числовом поле — осмысленное значение («потолка нет»), поэтому не подставляем 0.
function ReferralConfigForm({
  fields,
  value,
  saving,
  onSave,
}: {
  fields: Record<string, ReferralFieldDef>;
  value: Record<string, string>;
  saving: boolean;
  onSave: (value: Record<string, string>) => void;
}) {
  const [draft, setDraft] = useState<Record<string, string>>(value);
  useEffect(() => setDraft(value), [value]);
  const dirty = Object.keys(fields).some((k) => (draft[k] ?? "") !== (value[k] ?? ""));
  const set = (key: string, v: string) => setDraft((d) => ({ ...d, [key]: v }));

  return (
    <Stack spacing={2}>
      {Object.entries(fields).map(([key, def]) => {
        if (def.kind === "bool") {
          return (
            <FormControlLabel
              key={key}
              control={
                <Switch
                  checked={(draft[key] ?? "") === "true"}
                  disabled={saving}
                  onChange={(e) => set(key, e.target.checked ? "true" : "false")}
                />
              }
              label={def.label}
            />
          );
        }
        if (def.kind === "select") {
          return (
            <FormControl key={key} fullWidth size="small">
              <InputLabel id={`ref-${key}`}>{def.label}</InputLabel>
              <Select
                labelId={`ref-${key}`}
                label={def.label}
                value={draft[key] ?? ""}
                disabled={saving}
                onChange={(e) => set(key, String(e.target.value))}
              >
                {(def.options ?? []).map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {def.option_labels?.[opt] ?? opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        return (
          <TextField
            key={key}
            label={def.label}
            type={def.kind === "number" ? "number" : "text"}
            multiline={def.kind === "textarea"}
            minRows={def.kind === "textarea" ? 4 : undefined}
            value={draft[key] ?? ""}
            disabled={saving}
            onChange={(e) => set(key, e.target.value)}
            fullWidth
            size="small"
          />
        );
      })}
      <Box>
        <button
          type="button"
          disabled={saving || !dirty}
          onClick={() => onSave(draft)}
          style={{ padding: "8px 16px", cursor: saving || !dirty ? "default" : "pointer" }}
        >
          {saving ? "Сохранение…" : "Сохранить"}
        </button>
      </Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Применяется сразу. Потолок наценки пустой — ограничения нет: агент может поставить любую
        надбавку. Итоговый курс его клиентов = базовая наценка + надбавка агента.
      </Typography>
    </Stack>
  );
}

// Строковая настройка: сохраняем по Enter/потере фокуса, пустая строка — валидное значение.
function TextSettingField({
  label,
  initial,
  saving,
  onSave,
}: {
  label: string;
  initial: string;
  saving: boolean;
  onSave: (value: string) => void;
}) {
  const [val, setVal] = useState(initial);
  useEffect(() => setVal(initial), [initial]);

  const commit = () => {
    if (val.trim() === initial.trim()) return;
    onSave(val.trim());
  };

  return (
    <TextField
      label={label}
      value={val}
      disabled={saving}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      fullWidth
      size="small"
    />
  );
}

function LimitField({
  label,
  initial,
  saving,
  onSave,
  min = 1,
}: {
  label: string;
  initial: number;
  saving: boolean;
  onSave: (value: number) => void;
  min?: number;
}) {
  const [val, setVal] = useState(String(initial));
  useEffect(() => setVal(String(initial)), [initial]);

  const commit = () => {
    const n = Number(val);
    if (!Number.isFinite(n) || n < min || n === initial) {
      setVal(String(initial));
      return;
    }
    onSave(n);
  };

  return (
    <TextField
      label={label}
      type="number"
      value={val}
      disabled={saving}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      fullWidth
    />
  );
}
